import React from 'react';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import MergeFieldInput from './MergeFieldInput';
import { TIMEZONE_OPTIONS } from '../../utils/defaultWorkflowSettings';

const PLATFORM_CATEGORIES = [
  { value: 'authentication', label: 'Authentication' },
  { value: 'agency_lifecycle', label: 'Agency Lifecycle' },
  { value: 'client_self_repair', label: 'Client / Self-Repair' },
  { value: 'support_tickets', label: 'Support / Tickets' },
  { value: 'billing', label: 'Billing' },
  { value: 'system_alerts', label: 'System Alerts' },
];

const SENDER_SCOPE_OPTIONS = [
  { value: 'platform', label: 'Platform Mailgun Only' },
  { value: 'agency', label: 'Agency Mailgun' },
  { value: 'agency_with_fallback', label: 'Agency with Platform Fallback' },
];

const LearnMore = ({ children }) => (
  <button type="button" className="text-blue-600 hover:underline">
    {children || 'Learn more'}
  </button>
);

const SettingToggle = ({ id, label, helper, checked, onCheckedChange, disabled }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-slate-100 last:border-0">
    <div className="space-y-1 pr-4">
      <Label htmlFor={id} className="text-sm font-medium text-slate-900">
        {label}
      </Label>
      <p className="text-xs text-slate-500 leading-relaxed">
        {helper} <LearnMore />
      </p>
    </div>
    <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
  </div>
);

const WorkflowSettingsTab = ({
  workflow,
  settings,
  onSettingsChange,
  onMetaChange,
  token,
  disabled = false,
  showPlatformFields = true,
}) => (
  <div className="mx-auto max-w-3xl space-y-6" data-testid="workflow-settings-tab">
    <div>
      <h2 className="text-2xl font-semibold text-slate-900">Workflow settings</h2>
      <p className="mt-1 text-sm text-slate-500">
        Configure how this workflow runs, including its default options and behavior.
      </p>
    </div>

    {showPlatformFields && (
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <h3 className="text-sm font-semibold text-slate-900">General</h3>
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-600">Workflow Name</Label>
          <Input
            value={workflow.name || ''}
            onChange={(e) => onMetaChange?.({ name: e.target.value })}
            disabled={disabled}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-600">Description</Label>
          <Input
            value={workflow.description || ''}
            onChange={(e) => onMetaChange?.({ description: e.target.value })}
            disabled={disabled}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-600">Category</Label>
          <Select
            value={workflow.category || 'authentication'}
            onValueChange={(value) => onMetaChange?.({ category: value })}
            disabled={disabled}
          >
            <SelectTrigger data-testid="workflow-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PLATFORM_CATEGORIES.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-600">Email Sender Scope</Label>
          <Select
            value={workflow.sender_scope || 'platform'}
            onValueChange={(value) => onMetaChange?.({ sender_scope: value })}
            disabled={disabled}
          >
            <SelectTrigger data-testid="workflow-sender-scope-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SENDER_SCOPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500">
            Determines which Mailgun configuration sends emails for this workflow.
          </p>
        </div>
      </div>
    )}

    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="mb-2 text-sm font-semibold text-slate-900">Contact</h3>
      <SettingToggle
        id="allow-reentry"
        label="Allow re-entry"
        checked={settings.allow_reentry}
        disabled={disabled}
        onCheckedChange={(value) => onSettingsChange({ allow_reentry: value })}
        helper="Allows a Contact to re-enter once it has left this workflow. If the Contact attempts to re-enter while it is still enrolled in this workflow, it will get skipped. Also if this workflow has appointment or invoice based triggers it will allow Contact to re-enter even if the Allow re-entry setting is disabled."
      />
      <SettingToggle
        id="allow-multiple-opportunities"
        label="Allow multiple opportunities"
        checked={settings.allow_multiple_opportunities}
        disabled={disabled}
        onCheckedChange={(value) => onSettingsChange({ allow_multiple_opportunities: value })}
        helper='Allows a contact with multiple opportunities to enter the workflow as separate executions. For each opportunity, the contact will have a distinct execution in the workflow. Even if "Allow re-entry" is disabled, multiple opportunities will still enter the workflow.'
      />
      <SettingToggle
        id="stop-on-response"
        label="Stop on response"
        checked={settings.stop_on_response}
        disabled={disabled}
        onCheckedChange={(value) => onSettingsChange({ stop_on_response: value })}
        helper="Ends workflow for a contact if the contact responds to a message that is sent from this workflow."
      />
    </div>

    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
      <h3 className="text-sm font-semibold text-slate-900">Communication</h3>
      <div className="space-y-1.5">
        <Label className="text-xs text-slate-600">Timezone</Label>
        <Select
          value={settings.timezone_mode}
          onValueChange={(value) => onSettingsChange({ timezone_mode: value })}
          disabled={disabled}
        >
          <SelectTrigger data-testid="workflow-timezone-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIMEZONE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500">
          Wait steps and Time window executions will proceed based on this timezone. <LearnMore />
        </p>
      </div>

      <SettingToggle
        id="specific-time-window"
        label="Specific time"
        checked={settings.time_window_enabled}
        disabled={disabled}
        onCheckedChange={(value) => onSettingsChange({ time_window_enabled: value })}
        helper="Restrict actions from being sent outside the window you define."
      />
      {settings.time_window_enabled && (
        <div className="grid grid-cols-2 gap-3 pl-1">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Start</Label>
            <Input
              type="time"
              value={settings.time_window?.start || '09:00'}
              onChange={(e) => onSettingsChange({
                time_window: { ...settings.time_window, start: e.target.value },
              })}
              disabled={disabled}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">End</Label>
            <Input
              type="time"
              value={settings.time_window?.end || '17:00'}
              onChange={(e) => onSettingsChange({
                time_window: { ...settings.time_window, end: e.target.value },
              })}
              disabled={disabled}
            />
          </div>
        </div>
      )}

      <div className="space-y-3 pt-2">
        <Label className="text-xs font-medium text-slate-700">Sender details</Label>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">From name</Label>
            <MergeFieldInput
              value={settings.default_from_name}
              onValueChange={(value) => onSettingsChange({ default_from_name: value })}
              disabled={disabled}
              data-testid="workflow-default-from-name"
              token={token}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">From email</Label>
            <MergeFieldInput
              value={settings.default_from_email}
              onValueChange={(value) => onSettingsChange({ default_from_email: value })}
              disabled={disabled}
              data-testid="workflow-default-from-email"
              token={token}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-600">From number</Label>
          <Select
            value={settings.default_from_number || 'none'}
            onValueChange={(value) => onSettingsChange({
              default_from_number: value === 'none' ? '' : value,
            })}
            disabled={disabled}
          >
            <SelectTrigger data-testid="workflow-default-from-number">
              <SelectValue placeholder="Select from number" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Select from number</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-slate-500">
          You can set a default From name and From email for emails. You can also override this information within your email actions. <LearnMore />
        </p>
      </div>

      <SettingToggle
        id="mark-as-read"
        label="Mark as read"
        checked={settings.mark_as_read}
        disabled={disabled}
        onCheckedChange={(value) => onSettingsChange({ mark_as_read: value })}
        helper="Toggle this on if you want the conversations that this workflow will interact with to be marked as read."
      />
    </div>
  </div>
);

export default WorkflowSettingsTab;
